/**
 * Backoffice CRM: the customer inbox (#1518, docs/new-features/customer_inbox_spec.md § Email client).
 *
 * hello@'s mail as inboxEmailSync copies it into inbox_threads. The table shows one status at a time, customers or
 * notifications; a thread opens as a page in the list's place (#1541), at #inbox?thread=<id>, so Back returns to the list. Every change goes through a callable (inbox_service.js): the rules let
 * the CRM read these collections and write none of them.
 *
 * Two kinds of "read": the mailbox's \Seen (shared, the unread dot; opening a thread sets it) and each admin's own
 * red "new" line, in the list (since they last looked at it) and in a thread (since they last opened it).
 */
import { setNavBadge } from "./ui/shell.js";
import { BADGE, BADGE_DOT } from "./ui/classes.js";
import { escapeHTML, familyCardHTML, familyLoadingHTML, familyMessageHTML } from "./familyDetailsPanel.js";
import DOMPurify from "dompurify";
import { mountComposer, destroyComposer, setAlsoAnswerable } from "./inboxComposer.js";
import { mountThreadExtras, resetThreadExtras, formatWhen } from "./inboxThreadExtras.js";
import { historyHref } from "./historyController.js";
import { withBusy } from "./ui/busy.js";
import { messageBodyHTML, isLongMessage } from "./inboxMarkdown.js";

const LIST_LIMIT = 300;
const AUTO_SYNC_MS = 2 * 60 * 1000;
/** The customer's other conversations drawn above and below the open one (#2660), and the messages kept of them. */
const MAX_TIMELINE_THREADS = 10;
const MAX_TIMELINE_MESSAGES = 200;
const STATUSES = ["open", "waiting", "handled", "spam"];
const STATUS_LABELS = { open: "Open", waiting: "Waiting", handled: "Handled", spam: "Spam", snoozed: "Snoozed" };
const STATUS_BADGES = { open: BADGE.paprika, waiting: BADGE.blue, handled: BADGE.green, spam: BADGE.gray, snoozed: BADGE.yellow };
const STATUS_DOTS = { open: BADGE_DOT.paprika, waiting: BADGE_DOT.blue, handled: BADGE_DOT.green, spam: BADGE_DOT.gray, snoozed: BADGE_DOT.yellow };

let firestoreCtx = null;
let threads = [];
/** The customers' threads waiting on us (#2660), for the list's "also waiting" mark: the Open tab's own, whichever tab is shown. */
let waitingThreads = [];
let activeStatus = "open";
let activeKind = "customers";
let activeSource = "all";
let searchTerm = "";
/** When this admin last looked at the list before this visit: where the list's red line goes. */
let listSeenAt = null;
let syncing = false;
/** The open thread: its document, messages, and where its red line was when it was opened. */
let current = null;
let familyState = { familyId: null, details: null, error: null, tab: "plan" };
/** Whether the Inbox section is on screen this visit (enterInbox/leaveInbox), and where the list was scrolled. */
let visiting = false;
let listScrollY = 0;

export async function loadInboxTemplate() {
  const container = document.getElementById("sectionInboxContainer");
  if (!container) return;
  try {
    const res = await fetch("templates/inbox.html");
    if (!res.ok) throw new Error(`HTTP error ${res.status}`);
    container.innerHTML = await res.text();
  } catch (err) {
    console.error("❌ Failed to load inbox.html template:", err);
    container.innerHTML = `<div class="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">Could not load the inbox template: ${escapeHTML(err.message)}</div>`;
  }
}

/**
 * Binds the controls. Reads nothing: the template is mounted before anyone has signed in, so app.js starts the reads
 * once an admin is in (#1333).
 */
export function initInboxController(ctx) {
  firestoreCtx = ctx;
  document.querySelectorAll("[data-inbox-status]").forEach((btn) => {
    btn.addEventListener("click", () => {
      activeStatus = btn.dataset.inboxStatus;
      fetchInboxList();
    });
  });
  document.querySelectorAll("[data-inbox-kind]").forEach((btn) => {
    btn.addEventListener("click", () => {
      activeKind = btn.dataset.inboxKind;
      renderInboxList();
    });
  });
  document.getElementById("inboxSourceFilter")?.addEventListener("change", (e) => {
    activeSource = e.target.value;
    renderInboxList();
  });
  document.getElementById("inboxSyncStatus")?.addEventListener("click", (e) => {
    if (e.target.closest?.("[data-import-feedback]")) importPastFeedback();
  });
  document.getElementById("inboxSearchInput")?.addEventListener("input", (e) => {
    searchTerm = e.target.value.trim().toLowerCase();
    renderInboxList();
  });
  document.getElementById("inboxSyncNowBtn")?.addEventListener("click", () => syncInboxNow({ quiet: false }));
  document.getElementById("inboxMarkUnreadBtn")?.addEventListener("click", markCurrentUnread);
  document.getElementById("inboxTableBody")?.addEventListener("click", (e) => {
    const row = e.target.closest?.("[data-inbox-thread]");
    if (row) openInboxThread(row.dataset.inboxThread);
  });
  document.getElementById("inboxThreadBody")?.addEventListener("click", onThreadBodyClick);
  document.getElementById("inboxThreadStatusGroup")?.addEventListener("click", (e) => {
    const btn = e.target.closest?.("[data-thread-status]");
    if (btn) setCurrentStatus(btn.dataset.threadStatus, btn);
  });
  document.getElementById("inboxBackBtn")?.addEventListener("click", backToList);
}

function call(name, data = {}) {
  const { functions, httpsCallable } = firestoreCtx || {};
  if (!functions || typeof httpsCallable !== "function") throw new Error("Cloud Functions are not available.");
  return httpsCallable(functions, name)(data).then((res) => res.data || {});
}

function myUid() {
  return firestoreCtx?.currentUid?.() || null;
}

/**
 * Opening the section: records that this admin is looking at the list (and keeps when they last did, for the red
 * line), reads the list, and checks the mailbox once. Opening a thread from #inbox?thread=<id> as well.
 */
export async function openInbox() {
  if (!firestoreCtx) return;
  try {
    const res = await call("setInboxListSeen");
    listSeenAt = res.previous || null;
  } catch (err) {
    console.warn("⚠️ setInboxListSeen failed:", err);
  }
  await fetchInboxList();
  routeInbox();
  syncInboxNow({ quiet: true });
}

/**
 * The section opened or its address changed (app.js on every switch to #inbox…). The first time this visit it reads
 * the list (openInbox); after that it only follows the address: a thread's page, or the list. Going back to the list
 * keeps the red line where it was: only a new visit moves it.
 */
export function enterInbox() {
  if (!firestoreCtx) return; // the template isn't bound yet: its init switches to the section again
  if (!visiting) {
    visiting = true;
    openInbox();
    return;
  }
  routeInbox();
}

/** Another section opened: the page closes (saving a draft), and the next visit starts afresh. */
export function leaveInbox() {
  if (!visiting) return;
  visiting = false;
  closeThreadPage();
}

function routeInbox() {
  const threadId = threadIdFromHash(globalThis.location?.hash);
  if (threadId) {
    if (!current || current.thread.id !== threadId) openInboxThread(threadId, { fromAddress: true });
  } else if (current || !document.getElementById("inboxThreadView")?.hidden) {
    closeThreadPage();
  }
}

/** ← Inbox: the browser's Back when the list is the page before, else to #inbox (a thread opened from a link). */
function backToList() {
  if (globalThis.history?.state?.inboxThread === current?.thread?.id) {
    globalThis.history.back(); // hashchange → enterInbox → routeInbox → closeThreadPage
    return;
  }
  globalThis.history?.pushState(null, "", "#inbox");
  closeThreadPage();
}

function showView(thread) {
  const list = document.getElementById("inboxListView");
  const page = document.getElementById("inboxThreadView");
  if (list) list.hidden = thread;
  if (page) page.hidden = !thread;
}

/** Back to the list as it was: the composer saves its draft, and the list returns to where it was scrolled. */
function closeThreadPage() {
  current = null;
  destroyComposer();
  resetThreadExtras();
  showView(false);
  renderInboxList();
  globalThis.scrollTo?.(0, listScrollY);
}

/** `#inbox?thread=<id>` → the id, else null. */
export function threadIdFromHash(hash) {
  const m = /^#inbox\?(?:.*&)?thread=([0-9a-f]{16,64})(?:&|$)/.exec(String(hash || ""));
  return m ? m[1] : null;
}

/** Reads the threads of the active status, the counts, and the sync's health. */
/** The Open tab's threads, for the waiting mark on the other tabs. A failure only loses the mark, never the list. */
async function fetchOpenThreads() {
  const { db, collection, query, where, orderBy, limit, getDocs } = firestoreCtx;
  try {
    const snap = await getDocs(query(collection(db, "inbox_threads"), where("status", "==", "open"), orderBy("last_message_at", "desc"), limit(LIST_LIMIT)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn("⚠️ Waiting threads unreadable:", err);
    return [];
  }
}

export async function fetchInboxList() {
  if (!firestoreCtx) return;
  const { db, collection, query, where, orderBy, limit, getDocs, getDoc, doc, getCountFromServer } = firestoreCtx;
  setPressed("[data-inbox-status]", "inboxStatus", activeStatus);
  try {
    const base = collection(db, "inbox_threads");
    const q = activeStatus === "all"
      ? query(base, orderBy("last_message_at", "desc"), limit(LIST_LIMIT))
      : query(base, where("status", "==", activeStatus), orderBy("last_message_at", "desc"), limit(LIST_LIMIT));
    const snap = await getDocs(q);
    threads = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    // Snoozed: the one waking first on top.
    if (activeStatus === "snoozed") threads.sort((a, b) => String(a.snooze?.until || "").localeCompare(String(b.snooze?.until || "")));
    waitingThreads = activeStatus === "open" ? threads : await fetchOpenThreads();
    renderInboxList();
  } catch (err) {
    console.error("❌ Failed to read the inbox:", err);
    const body = document.getElementById("inboxTableBody");
    if (body) body.innerHTML = `<tr><td colspan="5" class="px-6 py-12 text-center text-sm text-red-600 dark:text-red-400">Could not read the inbox: ${escapeHTML(err.message)}</td></tr>`;
  }

  // Counts: customers per status, and the notifications still open.
  if (typeof getCountFromServer === "function") {
    const count = async (filters) => {
      const res = await getCountFromServer(query(collection(db, "inbox_threads"), ...filters.map(([f, v]) => where(f, "==", v))));
      return res.data().count;
    };
    try {
      const [open, waiting, handled, notifications, snoozed] = await Promise.all([
        count([["status", "open"], ["automated", false]]),
        count([["status", "waiting"], ["automated", false]]),
        count([["status", "handled"], ["automated", false]]),
        count([["status", "open"], ["automated", true]]),
        count([["status", "snoozed"], ["automated", false]])
      ]);
      setText("inboxCountSnoozed", snoozed);
      setText("inboxCountOpen", open);
      setText("inboxCountWaiting", waiting);
      setText("inboxCountHandled", handled);
      setText("inboxCountNotifications", notifications);
      setNavBadge("inbox", open > 0 ? String(open) : "", open > 0 ? "danger" : "neutral");
    } catch (err) {
      console.warn("⚠️ Inbox counts failed:", err);
    }
  }

  try {
    const ids = ["email", "app_store", "google_play", "app_feedback"];
    const snaps = await Promise.all(ids.map((id) => getDoc(doc(db, "inbox_sources", id))));
    const sources = {};
    ids.forEach((id, i) => { sources[id] = snaps[i].exists() ? snaps[i].data() : null; });
    const el = document.getElementById("inboxSyncStatus");
    if (el) el.innerHTML = sourcesStatusHTML(sources);
  } catch (err) {
    console.warn("⚠️ Inbox sync status unreadable:", err);
  }
}

/** Runs the sync now. Quiet (on open and every 2 minutes): no toast, the status line says how it went. */
export async function syncInboxNow({ quiet = true } = {}) {
  if (!firestoreCtx || syncing) return;
  syncing = true;
  const btn = document.getElementById("inboxSyncNowBtn");
  if (btn) btn.disabled = true;
  const toast = quiet ? null : firestoreCtx.showToast?.("Checking hello@ for new mail…", "processing");
  try {
    const summary = await call("syncInboxNow");
    if (toast) firestoreCtx.dismissToast?.(toast);
    const changed = (summary.stored || 0) + (summary.flag_updates || 0);
    if (!quiet) {
      firestoreCtx.showToast?.(summary.skipped === "locked" ? "A check is already running." :
        changed ? `${summary.stored || 0} new message(s).` : "No new mail.", "info");
    }
    if (changed || !quiet) await fetchInboxList();
    if (changed && current) await openInboxThread(current.thread.id, { refresh: true });
  } catch (err) {
    if (toast) firestoreCtx.dismissToast?.(toast);
    if (!quiet) firestoreCtx.showToast?.(`Couldn't check the mailbox: ${escapeHTML(err.message)}`, "error");
    await fetchInboxList();
  } finally {
    syncing = false;
    if (btn) btn.disabled = false;
  }
}

/**
 * The 2-minute check while the Inbox is on screen and the tab is visible. Returns the stop function app.js runs at
 * sign-out (loadForAdmin).
 */
export function startInboxAutoSync() {
  const handle = setInterval(() => {
    const section = document.getElementById("sectionInbox");
    if (!section || section.style.display === "none" || document.visibilityState === "hidden") return;
    syncInboxNow({ quiet: true });
  }, AUTO_SYNC_MS);
  return () => clearInterval(handle);
}

const SOURCE_LABELS = { email: "Email", app_feedback: "In-app feedback", app_store: "App Store", google_play: "Google Play" };

/**
 * Where a thread or a message came from (#1541): an outlined badge with the source's icon and colour, unlike the
 * filled status badges. Generic icons only, never the stores' logos.
 */
const ENVELOPE = `<path d="M3 4a2 2 0 0 0-2 2v1.161l8.441 4.221a1.25 1.25 0 0 0 1.118 0L19 7.162V6a2 2 0 0 0-2-2H3Z" /><path d="m19 8.839-7.77 3.885a2.75 2.75 0 0 1-2.46 0L1 8.839V14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8.839Z" />`;
const CHAT = `<path fill-rule="evenodd" clip-rule="evenodd" d="M10 2c-2.236 0-4.43.18-6.57.524C1.993 2.755 1 4.014 1 5.426v5.148c0 1.413.993 2.67 2.43 2.902.848.137 1.705.248 2.57.331v3.443a.75.75 0 0 0 1.28.53l3.58-3.579a.78.78 0 0 1 .527-.224 41.202 41.202 0 0 0 5.183-.5c1.437-.232 2.43-1.49 2.43-2.903V5.426c0-1.413-.993-2.67-2.43-2.902A41.289 41.289 0 0 0 10 2Zm0 7a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM8 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm5 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" />`;
const STAR = `<path fill-rule="evenodd" clip-rule="evenodd" d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401Z" />`;
const SOURCE_STYLES = {
  email: { icon: ENVELOPE, color: "text-sky-600 dark:text-sky-400" },
  app_feedback: { icon: CHAT, color: "text-violet-600 dark:text-violet-400" },
  app_store: { icon: STAR, color: "text-blue-600 dark:text-blue-400" },
  google_play: { icon: STAR, color: "text-emerald-600 dark:text-emerald-400" }
};

/** The badge for a source; `label` overrides its name (a message's "App Store review"). */
export function sourceBadgeHTML(source, { label = null, size = "sm" } = {}) {
  const key = SOURCE_STYLES[source] ? source : "email";
  const { icon, color } = SOURCE_STYLES[key];
  const box = size === "md" ? "gap-x-1.5 px-2 py-1 text-sm" : "gap-x-1 px-1.5 py-0.5 text-xs";
  return `<span data-inbox-source="${key}" class="inline-flex shrink-0 items-center rounded-md bg-white font-medium whitespace-nowrap text-gray-700 inset-ring inset-ring-gray-300 dark:bg-white/5 dark:text-gray-200 dark:inset-ring-white/15 ${box}"><svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" class="${size === "md" ? "size-4" : "size-3.5"} ${color}">${icon}</svg>${escapeHTML(label || SOURCE_LABELS[key])}</span>`;
}

/**
 * How one message came or went: an email (a reply to a feedback goes by email too), the in-app feedback, a store
 * review, or our public reply to one.
 */
export function messageSource(m) {
  const store = (m.review && m.review.store) || (m.review_reply && m.review_reply.store);
  const storeName = store === "google_play" ? "Google Play" : "App Store";
  if (m.feedback) return { source: "app_feedback", label: "In-app feedback" };
  if (m.review) return { source: store === "google_play" ? "google_play" : "app_store", label: `${storeName} review` };
  if (m.review_reply) return { source: store === "google_play" ? "google_play" : "app_store", label: `Public reply on ${storeName}` };
  return { source: "email", label: "Email" };
}

/**
 * One line per source: when it last synced, or its error and what to fix. The feedback line offers the one-off import
 * of feedback written before the inbox.
 */
export function sourcesStatusHTML(sources, now = new Date()) {
  const line = (id, s, okText) => {
    const label = `<span class="font-medium text-gray-700 dark:text-gray-300">${SOURCE_LABELS[id]}</span>`;
    if (!s || !s.last_run_at) return `<li>${label}: not synced yet.</li>`;
    if (s.last_error) {
      return `<li class="text-red-600 dark:text-red-400">${label}: last check ${escapeHTML(relativeTime(s.last_run_at, now))} failed: ${escapeHTML(s.last_error)}${s.last_ok_at ? ` Last good one ${escapeHTML(relativeTime(s.last_ok_at, now))}.` : ""}</li>`;
    }
    return `<li>${label}: ${okText(s)}</li>`;
  };
  const items = [
    line("email", sources.email, (s) => `checked ${escapeHTML(relativeTime(s.last_ok_at, now))}.`),
    scheduledEmailLine(sources.email, now),
    line("app_store", sources.app_store, (s) => `checked ${escapeHTML(relativeTime(s.last_ok_at, now))} (hourly).`),
    line("google_play", sources.google_play, (s) => `checked ${escapeHTML(relativeTime(s.last_ok_at, now))} (hourly).`),
    sources.app_feedback && sources.app_feedback.imported_at
      ? `<li><span class="font-medium text-gray-700 dark:text-gray-300">In-app feedback</span>: arrives as it's sent; older feedback imported ${escapeHTML(relativeTime(sources.app_feedback.imported_at, now))}.</li>`
      : `<li><span class="font-medium text-gray-700 dark:text-gray-300">In-app feedback</span>: arrives as it's sent. <button type="button" data-import-feedback class="font-semibold text-paprika-700 hover:underline dark:text-paprika-400">Import past feedback</button></li>`
  ];
  return items.join("");
}

const SCHEDULE_OVERDUE_MS = 45 * 60 * 1000;

/**
 * The backend's own 30-minute inboxEmailSync, apart from this tab's checks (#1729): the line above moves whenever the
 * Inbox is open, so it can't show whether the schedule runs. Red when its last run failed or is overdue.
 */
function scheduledEmailLine(s, now) {
  const label = `<span class="font-medium text-gray-700 dark:text-gray-300">Scheduled email check</span>`;
  if (!s || !s.schedule_last_run_at) return `<li>${label}: no run recorded yet (every 30 min).</li>`;
  const ran = escapeHTML(relativeTime(s.schedule_last_run_at, now));
  if (s.schedule_last_error) {
    return `<li class="text-red-600 dark:text-red-400">${label}: last run ${ran} failed: ${escapeHTML(s.schedule_last_error)}${s.schedule_last_ok_at ? ` Last good one ${escapeHTML(relativeTime(s.schedule_last_ok_at, now))}.` : ""}</li>`;
  }
  if (now.getTime() - Date.parse(s.schedule_last_run_at) > SCHEDULE_OVERDUE_MS) {
    return `<li class="text-red-600 dark:text-red-400">${label}: last ran ${ran}, overdue (every 30 min).</li>`;
  }
  return `<li>${label}: ran ${ran} (every 30 min).</li>`;
}

async function importPastFeedback() {
  const toast = firestoreCtx.showToast?.("Importing past feedback…", "processing");
  try {
    const res = await call("importInboxFeedback");
    if (toast) firestoreCtx.dismissToast?.(toast);
    firestoreCtx.showToast?.(`Imported ${res.created} of ${res.feedback} feedback.`, "success");
    fetchInboxList();
  } catch (err) {
    if (toast) firestoreCtx.dismissToast?.(toast);
    firestoreCtx.showToast?.(`Couldn't import: ${escapeHTML(err.message)}`, "error");
  }
}

/** The threads the table shows: the active kind, then the search. */
export function visibleThreads(all, { kind, term, source = "all" }) {
  return all.filter((t) => (kind === "notifications" ? t.automated === true : t.automated !== true))
    .filter((t) => source === "all" || (t.source || "email") === source)
    .filter((t) => {
      if (!term) return true;
      const hay = [t.customer?.name, t.customer?.email, t.subject, t.snippet].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(term);
    });
}

/**
 * The table's rows, newest first, with this admin's red line above the first thread that has had nothing new since
 * they last looked. No line on a first visit, or when everything (or nothing) is new.
 */
export function inboxRowsHTML(list, { listSeenAt: seenAt, now = new Date(), waiting = [] } = {}) {
  if (!list.length) {
    return `<tr><td colspan="5" class="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">Nothing here.</td></tr>`;
  }
  const firstOld = seenAt ? list.findIndex((t) => !(t.last_message_at > seenAt)) : -1;
  return list.map((t, i) => {
    const line = i === firstOld && firstOld > 0
      ? `<tr data-inbox-new-line aria-hidden="true"><td colspan="5" class="p-0"><div class="flex items-center gap-2 px-4 sm:px-6"><span class="h-px flex-1 bg-red-500"></span><span class="py-0.5 text-xs font-semibold text-red-600 dark:text-red-400">New since you last looked</span><span class="h-px flex-1 bg-red-500"></span></div></td></tr>`
      : "";
    return line + rowHTML(t, now, waiting);
  }).join("");
}

const STATUS_WORDS = { open: "Open", waiting: "Waiting", handled: "Handled", spam: "Spam", snoozed: "Snoozed" };

/** The earlier conversations a thread records (#2021), or null: `{ count, last }` with count above 0. */
function priorContactOf(t) {
  const p = t && t.prior_contact;
  return p && Number(p.count) > 0 ? { count: Number(p.count), last: p.last || null } : null;
}

function conversations(n) {
  return `${n} earlier conversation${n === 1 ? "" : "s"}`;
}

/** Beside the sender's name in the list: that they wrote to us before, and how often; the last time in the tooltip. */
export function priorContactBadgeHTML(t) {
  const prior = priorContactOf(t);
  if (!prior) return "";
  const when = prior.last?.last_message_at ? `, the last on ${formatWhen(prior.last.last_message_at)}` : "";
  return `<span class="ml-1.5 inline-flex items-center rounded-sm bg-sky-50 px-1 text-[0.65rem] font-semibold whitespace-nowrap text-sky-700 dark:bg-sky-500/10 dark:text-sky-300" title="${escapeHTML(`Has written to us before: ${conversations(prior.count)}${when}`)}">wrote before · ${prior.count}</span>`;
}

/**
 * Under the thread's header: their earlier conversations, the last one's subject, status and date with a button that
 * opens it, and the link to their full contact history (#1925). From the thread document, so it is there before the
 * timeline has loaded. Empty when they never wrote before.
 */
export function priorContactBannerHTML(t) {
  const prior = priorContactOf(t);
  if (!prior) return "";
  const last = prior.last;
  const person = t.user_uid ? { uid: t.user_uid } : t.customer_email ? { email: t.customer_email } : null;
  const lastLine = last
    ? ` The last: <button type="button" data-open-thread="${escapeHTML(last.thread_id || "")}" class="font-semibold text-sky-900 underline decoration-sky-400 underline-offset-2 hover:decoration-sky-700 dark:text-sky-100">${escapeHTML(last.subject || "(no subject)")}</button> <span class="text-sky-700 dark:text-sky-300">· ${escapeHTML(STATUS_WORDS[last.status] || last.status || "")}${last.last_message_at ? ` · ${escapeHTML(formatWhen(last.last_message_at))}` : ""}</span>`
    : "";
  const history = person
    ? `<a href="${escapeHTML(historyHref(person))}" class="rounded-md bg-white/70 px-2.5 py-1 text-sm font-semibold whitespace-nowrap text-sky-900 hover:bg-white dark:bg-white/10 dark:text-sky-100 dark:hover:bg-white/20">Full contact history →</a>`
    : "";
  return `<span><span class="font-semibold">They have written to us before:</span> ${conversations(prior.count)}.${lastLine}</span>${history}`;
}

function rowHTML(t, now, waiting = []) {
  const unread = (t.unread || 0) > 0;
  const source = t.source || "email";
  const name = t.customer?.name || t.customer?.email || (source === "app_feedback" ? "Anonymous (signed out)" : "(unknown sender)");
  const email = t.customer?.name ? t.customer?.email || "" : "";
  const status = STATUS_LABELS[t.status] ? t.status : "open";
  const account = (t.user_uid
    ? `<span class="ml-1.5 inline-flex items-center rounded-sm bg-paprika-50 px-1 text-[0.65rem] font-semibold text-paprika-700 dark:bg-paprika-500/10 dark:text-paprika-300" title="Has a WeeknightBite account">account</span>`
    : "") + priorContactBadgeHTML(t) + waitingBadgeHTML(t, waiting, now);
  const clip = t.has_attachments
    ? `<svg viewBox="0 0 20 20" fill="currentColor" aria-label="Has attachments" class="inline size-4 shrink-0 text-gray-400"><path d="M15.621 4.379a3 3 0 0 0-4.242 0l-7 7a3 3 0 0 0 4.241 4.243h.001l.497-.5a.75.75 0 0 1 1.064 1.057l-.498.501-.002.002a4.5 4.5 0 0 1-6.364-6.364l7-7a4.5 4.5 0 0 1 6.368 6.36l-3.455 3.553A2.625 2.625 0 1 1 9.52 9.52l3.45-3.451a.75.75 0 1 1 1.061 1.06l-3.45 3.451a1.125 1.125 0 0 0 1.587 1.595l3.454-3.553a3 3 0 0 0 0-4.243Z" fill-rule="evenodd" clip-rule="evenodd" /></svg>`
    : "";
  return `<tr data-inbox-thread="${escapeHTML(t.id)}" class="cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5">
    <td class="py-3 pr-3 pl-4 text-sm sm:pl-6">
      <div class="flex items-center gap-2">
        <span aria-hidden="true" class="${unread ? "size-2 shrink-0 rounded-full bg-paprika-600 dark:bg-paprika-400" : "size-2 shrink-0"}"></span>
        <div class="min-w-0">
          <p class="${unread ? "truncate font-semibold text-gray-900 dark:text-white" : "truncate font-medium text-gray-700 dark:text-gray-300"}">${escapeHTML(name)}${account}</p>
          <p class="truncate text-xs text-gray-500 dark:text-gray-400">${escapeHTML(email)}</p>
        </div>
      </div>
      ${unread ? `<span class="sr-only">Unread</span>` : ""}
    </td>
    <td class="px-3 py-3 text-sm">${sourceBadgeHTML(source)}</td>
    <td class="max-w-0 px-3 py-3 text-sm">
      <p class="${unread ? "flex items-center gap-1.5 truncate font-semibold text-gray-900 dark:text-white" : "flex items-center gap-1.5 truncate text-gray-900 dark:text-white"}"><span class="truncate">${escapeHTML(t.subject || "(no subject)")}</span>${clip}<span class="shrink-0 text-xs font-normal text-gray-400">${t.message_count || 0}</span></p>
      <p class="truncate text-gray-500 dark:text-gray-400">${t.last_direction === "out" ? "You: " : ""}${escapeHTML(t.snippet || "")}</p>
    </td>
    <td class="px-3 py-3 text-sm"><span class="${STATUS_BADGES[status]}"><span aria-hidden="true" class="${STATUS_DOTS[status]}"></span>${STATUS_LABELS[status]}</span>${status === "snoozed" && t.snooze?.until ? `<p class="mt-1 text-xs whitespace-nowrap text-gray-500 dark:text-gray-400">until ${escapeHTML(formatWhen(t.snooze.until))}</p>` : ""}</td>
    <td class="py-3 pr-4 pl-3 text-right text-sm whitespace-nowrap text-gray-500 sm:pr-6 dark:text-gray-400" title="${escapeHTML(fullTime(t.last_message_at))}">${escapeHTML(relativeTime(t.last_message_at, now))}</td>
  </tr>`;
}

/** Whether a thread waits on us (#2660): the customer wrote last and nobody has answered. Snoozed is a decision to wait. */
export function waitingOnUs(t) {
  return !!t && t.status === "open" && t.last_direction === "in" && t.automated !== true;
}

function customerKey(t) {
  return String((t && (t.customer_email || t.customer?.email)) || "").trim().toLowerCase();
}

/** The same customer's other threads that wait on us, the one waiting longest first: same address or same account. */
export function waitingElsewhere(t, threadsOfCustomer) {
  if (!t) return [];
  const email = customerKey(t);
  const uid = t.user_uid || "";
  if (!email && !uid) return [];
  return (threadsOfCustomer || [])
    .filter((o) => o.id !== t.id && waitingOnUs(o) && ((email && customerKey(o) === email) || (uid && o.user_uid === uid)))
    .sort((a, b) => String(a.last_message_at || "").localeCompare(String(b.last_message_at || "")));
}

/** Beside the sender's name in the list: another conversation of theirs is still waiting on us, and since when. */
export function waitingBadgeHTML(t, waiting, now = new Date()) {
  const others = waitingElsewhere(t, waiting);
  if (!others.length) return "";
  const since = others.length === 1 ? ` · ${relativeTime(others[0].last_message_at, now)}` : ` · ${others.length}`;
  const title = others.length === 1
    ? `Another conversation of theirs is still waiting on us: ${others[0].subject || "(no subject)"}`
    : `${others.length} other conversations of theirs are still waiting on us`;
  return `<span class="ml-1.5 inline-flex items-center rounded-sm bg-amber-50 px-1 text-[0.65rem] font-semibold whitespace-nowrap text-amber-800 dark:bg-amber-500/10 dark:text-amber-300" title="${escapeHTML(title)}">also waiting${escapeHTML(since)}</span>`;
}

/**
 * Under the thread's header, in place of the "wrote before" banner: the customer's other conversation(s) waiting on
 * us, the one waiting longest first, with a button that opens it and the link to their contact history. Empty when
 * nothing else waits.
 */
export function waitingBannerHTML(t, others) {
  if (!others.length) return "";
  const first = others[0];
  const person = t.user_uid ? { uid: t.user_uid } : customerKey(t) ? { email: customerKey(t) } : null;
  const more = others.length > 1 ? ` and ${others.length - 1} more` : "";
  const history = person
    ? `<a href="${escapeHTML(historyHref(person))}" class="rounded-md bg-white/70 px-2.5 py-1 text-sm font-semibold whitespace-nowrap text-amber-900 hover:bg-white dark:bg-white/10 dark:text-amber-100 dark:hover:bg-white/20">Full contact history →</a>`
    : "";
  return `<span><span class="font-semibold">Another conversation is still waiting on us:</span> <button type="button" data-open-thread="${escapeHTML(first.id)}" class="font-semibold underline decoration-amber-400 underline-offset-2 hover:decoration-amber-700 dark:text-amber-100">${escapeHTML(first.subject || "(no subject)")}</button> <span class="text-amber-700 dark:text-amber-300">· since ${escapeHTML(formatWhen(first.last_message_at))}</span>${more}.</span>${history}`;
}

/** This customer's other threads, from the same two keys the "wrote before" count uses; [] when there is no key. */
async function fetchCustomerThreads(t) {
  const { db, collection, query, where, getDocs } = firestoreCtx;
  const email = customerKey(t);
  const reads = [];
  if (email) reads.push(getDocs(query(collection(db, "inbox_threads"), where("customer_email", "==", t.customer_email || t.customer?.email))));
  if (t.user_uid) reads.push(getDocs(query(collection(db, "inbox_threads"), where("user_uid", "==", t.user_uid))));
  const byId = new Map();
  (await Promise.all(reads)).forEach((snap) => snap.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() })));
  return [...byId.values()];
}

/** Draws the waiting banner; the "wrote before" banner stays if nothing else waits. */
function drawWaitingBanner(thread, others) {
  const banner = document.getElementById("inboxWaitingBanner");
  if (!banner) return;
  const html = waitingBannerHTML(thread, others);
  banner.innerHTML = html;
  banner.hidden = !html;
  const prior = document.getElementById("inboxPriorContactBanner");
  if (prior && html) prior.hidden = true;
  banner.querySelector("[data-open-thread]")?.addEventListener("click", (e) => openInboxThread(e.currentTarget.dataset.openThread));
}

/**
 * Reads the customer's other threads once the page is up: the waiting banner, and their messages for the timeline (#2660).
 * A failure only leaves the page as the one thread it was. The page keeps the place the reader was at.
 */
async function loadCustomer(thread) {
  const { db, collection, query, orderBy, getDocs } = firestoreCtx;
  let all = [];
  try {
    all = await fetchCustomerThreads(thread);
  } catch (err) {
    console.warn("⚠️ The customer's threads are unreadable:", err);
  }
  if (!current || current.thread.id !== thread.id) return;
  const waitingOthers = waitingElsewhere(thread, all);
  drawWaitingBanner(thread, waitingOthers);
  setAlsoAnswerable(thread.id, waitingOthers);
  if (thread.automated) return;
  const chosen = all.filter((o) => o.id !== thread.id && o.automated !== true)
    .sort((a, b) => String(b.last_message_at || "").localeCompare(String(a.last_message_at || "")))
    .slice(0, MAX_TIMELINE_THREADS);
  const loaded = (await Promise.all(chosen.map(async (o) => {
    try {
      const snap = await getDocs(query(collection(db, "inbox_threads", o.id, "messages"), orderBy("sent_at", "asc")));
      return { thread: o, messages: snap.docs.map((d) => ({ id: d.id, threadId: o.id, ...d.data() })) };
    } catch (err) {
      console.warn("⚠️ A conversation's messages are unreadable:", err);
      return null;
    }
  }))).filter(Boolean);
  if (!current || current.thread.id !== thread.id) return;
  const mine = current.messages[current.messages.length - 1];
  const spot = () => (mine ? document.querySelector(`[data-inbox-message-id="${CSS.escape(mine.id)}"]`) : null)?.getBoundingClientRect?.().top;
  const before = spot();
  current.others = loaded;
  current.hiddenThreads = Math.max(0, all.filter((o) => o.id !== thread.id && o.automated !== true).length - chosen.length);
  renderThread();
  const after = spot();
  if (before !== undefined && after !== undefined && Math.abs(after - before) > 1) globalThis.scrollBy?.(0, after - before);
}

export function renderInboxList() {
  setPressed("[data-inbox-kind]", "inboxKind", activeKind);
  const body = document.getElementById("inboxTableBody");
  if (!body) return;
  body.innerHTML = inboxRowsHTML(visibleThreads(threads, { kind: activeKind, term: searchTerm, source: activeSource }), { listSeenAt, waiting: waitingThreads });
}

/**
 * Opens a thread as a page, then marks it read in the mailbox and moves this admin's marker. From the list (or another
 * thread) it adds #inbox?thread=<id> to the history, so Back returns; `fromAddress` is the address already saying so.
 */
export async function openInboxThread(threadId, { refresh = false, fromAddress = false } = {}) {
  if (!firestoreCtx || !/^[0-9a-f]{16,64}$/.test(String(threadId))) return;
  const { db, doc, getDoc, collection, query, orderBy, getDocs } = firestoreCtx;
  const bodyEl = document.getElementById("inboxThreadBody");
  if (!refresh) {
    if (!fromAddress) globalThis.history?.pushState({ inboxThread: threadId }, "", `#inbox?thread=${threadId}`);
    if (document.getElementById("inboxThreadView")?.hidden) listScrollY = globalThis.scrollY || 0;
    if (current && current.thread.id !== threadId) {
      await destroyComposer();
      resetThreadExtras();
    }
    current = null;
    setText("inboxThreadTitle", "Conversation");
    setText("inboxThreadCustomer", "");
    const source = document.getElementById("inboxThreadSource");
    if (source) source.innerHTML = "";
    const side = document.getElementById("inboxThreadSide");
    if (side) side.innerHTML = "";
    const waitingBanner = document.getElementById("inboxWaitingBanner");
    if (waitingBanner) { waitingBanner.innerHTML = ""; waitingBanner.hidden = true; }
    if (bodyEl) bodyEl.innerHTML = `<div class="py-12 text-center text-sm text-gray-500 dark:text-gray-400"><span class="mx-auto mb-3 block size-6 animate-spin rounded-full border-2 border-gray-300 border-t-paprika-600 dark:border-white/20 dark:border-t-paprika-400"></span>Loading the conversation…</div>`;
    showView(true);
    globalThis.scrollTo?.(0, 0);
  }
  try {
    const [threadSnap, messagesSnap] = await Promise.all([
      getDoc(doc(db, "inbox_threads", threadId)),
      getDocs(query(collection(db, "inbox_threads", threadId, "messages"), orderBy("sent_at", "asc")))
    ]);
    if (!threadSnap.exists()) throw new Error("This conversation no longer exists.");
    const thread = { id: threadId, ...threadSnap.data() };
    const messages = messagesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    // The line stays where it was when the page opened, so it can be read down to (refreshes keep it).
    const lineAfter = refresh && current && current.thread.id === threadId ? current.lineAfter
      : (thread.seen_by && thread.seen_by[myUid()]) || null;
    const same = Boolean(current && refresh && current.thread.id === threadId);
    current = { thread, messages, lineAfter, shown: current && refresh ? current.shown : {}, others: same ? current.others : [], expanded: same ? current.expanded : {}, hiddenThreads: same ? current.hiddenThreads : 0 };
    renderThread({ refetch: true });
    if (!refresh) scrollToNewest();
    mountComposer({
      ctx: firestoreCtx, thread, myUid: myUid(),
      onSent: (id) => {
        if (current && current.thread.id === id) openInboxThread(id, { refresh: true }).then(scrollToNewest);
        fetchInboxList();
      }
    });
    if (!refresh || messages.some((m) => m.direction === "in" && !m.seen)) markCurrentRead();
  } catch (err) {
    console.error("❌ Failed to open the conversation:", err);
    if (bodyEl) bodyEl.innerHTML = `<p class="text-sm text-red-600 dark:text-red-400">${escapeHTML(err.message)}</p>`;
  }
}

/** Opens at the red "New" line, else at the newest message: the part that needs reading. A short thread stays at the top. */
function scrollToNewest() {
  const body = document.getElementById("inboxThreadBody");
  if (!body) return;
  const target = body.querySelector("[data-inbox-thread-new-line]") || [...body.querySelectorAll("[data-inbox-message-id]")].pop();
  if (!target || typeof target.getBoundingClientRect !== "function") return;
  const top = target.getBoundingClientRect().top;
  // Below the sticky top bar (4rem), and only when it is off screen.
  if (top > (globalThis.innerHeight || 0) - 120) globalThis.scrollBy?.(0, top - 80);
}

async function markCurrentRead() {
  if (!current) return;
  const threadId = current.thread.id;
  try {
    const res = await call("markInboxThreadRead", { threadId });
    const row = threads.find((t) => t.id === threadId);
    if (row) {
      if (res.mailbox_updated) row.unread = 0;
      row.seen_by = { ...(row.seen_by || {}), [myUid()]: row.last_message_at };
    }
    if (current && current.thread.id === threadId && res.mailbox_updated) {
      current.messages.forEach((m) => { if (m.direction === "in") m.seen = true; });
      current.thread.unread = 0;
    }
    if (!res.mailbox_updated) firestoreCtx.showToast?.("Couldn't mark it read in the mailbox; it stays unread there.", "warning");
  } catch (err) {
    console.warn("⚠️ markInboxThreadRead failed:", err);
  }
}

async function markCurrentUnread() {
  if (!current) return;
  const threadId = current.thread.id;
  await withBusy(document.getElementById("inboxMarkUnreadBtn"), async () => {
    try {
      const res = await call("markInboxThreadUnread", { threadId });
      const row = threads.find((t) => t.id === threadId);
      if (row) row.unread = res.unread;
      if (current && current.thread.id === threadId) current.thread.unread = res.unread;
      firestoreCtx.showToast?.("Marked unread, here and in the mailbox.", "success");
    } catch (err) {
      firestoreCtx.showToast?.(`Couldn't mark it unread: ${escapeHTML(err.message)}`, "error");
    }
  });
}

async function setCurrentStatus(status, button = null) {
  if (!current || !STATUSES.includes(status) || current.thread.status === status) return;
  const threadId = current.thread.id;
  const others = [...(document.getElementById("inboxThreadStatusGroup")?.querySelectorAll("[data-thread-status]") || [])];
  await withBusy(button, async () => {
    try {
      await call("setInboxThreadStatus", { threadId, status });
      const row = threads.find((t) => t.id === threadId);
      if (row) row.status = status;
      if (current && current.thread.id === threadId) {
        current.thread.status = status;
        renderStatusGroup();
      }
      firestoreCtx.showToast?.(`Moved to ${STATUS_LABELS[status]}.`, "success");
      fetchInboxList();
    } catch (err) {
      firestoreCtx.showToast?.(`Couldn't change the status: ${escapeHTML(err.message)}`, "error");
    }
  }, { others });
}

function renderThread({ refetch = false } = {}) {
  if (!current) return;
  const { thread, messages, lineAfter, shown } = current;
  setText("inboxThreadTitle", thread.subject || "(no subject)");
  setText("inboxThreadCustomer", threadHeaderWho(thread));
  const source = document.getElementById("inboxThreadSource");
  if (source) source.innerHTML = sourceBadgeHTML(thread.source || "email", { size: "md" });
  renderStatusGroup();
  const priorBanner = document.getElementById("inboxPriorContactBanner");
  if (priorBanner) {
    const html = priorContactBannerHTML(thread);
    priorBanner.innerHTML = html;
    priorBanner.hidden = !html || !document.getElementById("inboxWaitingBanner")?.hidden;
    priorBanner.querySelector("[data-open-thread]")?.addEventListener("click", (e) => openInboxThread(e.currentTarget.dataset.openThread));
  }
  if (refetch) loadCustomer(thread);
  const body = document.getElementById("inboxThreadBody");
  if (!body) return;
  body.innerHTML = current.others?.length
    ? customerTimelineHTML(thread, messages, current.others, { lineAfter, shown, myUid: myUid(), expanded: current.expanded || {}, hiddenThreads: current.hiddenThreads || 0 })
    : threadMessagesHTML(messages, { lineAfter, shown, myUid: myUid() });
  fitMessageBodies(body);
  const side = document.getElementById("inboxThreadSide");
  if (side) side.innerHTML = customerPanelHTML(thread) + `<div id="inboxThreadExtras"></div>`;
  if (thread.user_uid && !thread.automated) loadFamily(thread.family_id || thread.user_uid);
  mountThreadExtras({
    ctx: firestoreCtx, thread, refetch,
    onChange: (change) => {
      if (change.open) { openInboxThread(change.open); return; }
      if (current && current.thread.id === thread.id) openInboxThread(thread.id, { refresh: true });
      fetchInboxList();
    }
  });
}

/**
 * A long message was drawn clamped: where it overflows, Show more appears; where it fits after all, the clamp and its
 * toggle go (#1695). Measured after drawing, since the bubble's width decides how tall the text is.
 */
function fitMessageBodies(container) {
  container.querySelectorAll("[data-inbox-clamped]").forEach((el) => {
    const toggle = container.querySelector(`[data-inbox-clamp-toggle][data-inbox-message="${CSS.escape(el.dataset.inboxBody)}"]`);
    if (el.scrollHeight > el.clientHeight + 1) {
      if (toggle) toggle.hidden = false;
      return;
    }
    el.removeAttribute("data-inbox-clamped");
    el.classList.remove("max-h-72", "overflow-hidden", "mask-b-from-75%");
    if (!toggle) return;
    const row = toggle.parentElement;
    const dot = toggle.nextElementSibling?.matches("[data-inbox-toggle-dot]") ? toggle.nextElementSibling : null;
    toggle.remove();
    dot?.remove();
    if (row && !row.children.length) row.remove();
  });
}

/** Who the thread is with, for the page's header: "Name <address>", the address, or the name (the source badge says where from). */
export function threadHeaderWho(t) {
  const c = t && t.customer;
  if (!c) return "";
  if (c.email) return c.name ? `${c.name} <${c.email}>` : c.email;
  return c.name || (t.source === "app_feedback" ? "Anonymous (signed out)" : "");
}

function renderStatusGroup() {
  const group = document.getElementById("inboxThreadStatusGroup");
  if (!group || !current) return;
  group.innerHTML = STATUSES.map((s) => `<button type="button" data-thread-status="${s}" aria-pressed="${current.thread.status === s}" class="relative -ml-px inline-flex items-center bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 inset-ring-1 inset-ring-gray-300 first:ml-0 first:rounded-l-md last:rounded-r-md hover:bg-gray-50 focus:z-10 disabled:cursor-wait aria-pressed:z-10 aria-pressed:bg-gray-900 aria-pressed:text-white aria-pressed:inset-ring-gray-900 aria-busy:bg-gray-100 dark:bg-white/10 dark:text-white dark:inset-ring-gray-700 dark:hover:bg-white/20 dark:aria-pressed:bg-white dark:aria-pressed:text-gray-900 dark:aria-pressed:inset-ring-white dark:aria-busy:bg-white/20">${STATUS_LABELS[s]}</button>`).join("");
}

/**
 * The conversation, oldest first, with this admin's red "New" line above the first message that arrived after their
 * marker. Never opened by them: above the first unread customer message, if any. A reply the other admin sent from
 * the CRM counts as new; one this admin sent doesn't, and neither does one found in Sent (nobody knows who wrote it).
 * @param {{ lineAfter: string|null, shown: Object<string, {quoted?: boolean, original?: boolean}>, myUid?: string }} options
 */
export function threadMessagesHTML(messages, { lineAfter = null, shown = {}, myUid: uid = null } = {}) {
  const countsAsNew = (m) => m.direction === "in" || Boolean(m.sent_by && m.sent_by.uid && m.sent_by.uid !== uid);
  const lineIndex = lineAfter
    ? messages.findIndex((m) => m.sent_at > lineAfter && countsAsNew(m))
    : messages.findIndex((m) => m.direction === "in" && !m.seen);
  return `<ol class="flex flex-col gap-5">${messages.map((m, i) => {
    const line = i === lineIndex
      ? `<li data-inbox-thread-new-line aria-label="New since you last opened this" class="flex items-center gap-2"><span class="h-px flex-1 bg-red-500"></span><span class="text-xs font-semibold text-red-600 dark:text-red-400">New</span><span class="h-px flex-1 bg-red-500"></span></li>`
      : "";
    return line + messageHTML(m, shown[m.id] || {});
  }).join("")}</ol>`;
}

function messageHTML(m, shown, { muted = false } = {}) {
  const ours = m.direction === "out";
  const via = messageSource(m);
  const who = m.from ? (m.from.name || m.from.email) : "";
  const text = shown.quoted ? m.body_text : (m.body_trimmed || m.body_text || "");
  const hasQuote = (m.body_text || "").trim() !== (m.body_trimmed || "").trim();
  // A long text starts collapsed; fitMessageBodies() keeps the clamp and shows Show more only if it overflows (#1695).
  const clamped = isLongMessage(text) && !shown.full;
  const attachments = (m.attachments || []).map((a) => `<button type="button" data-inbox-attachment="${escapeHTML(a.part)}" data-inbox-message="${escapeHTML(m.id)}" class="inline-flex items-center gap-x-1.5 rounded-md bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-gray-200 dark:inset-ring-white/10 dark:hover:bg-white/20" title="Download (opens nothing in the CRM)"><svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" class="size-4 text-gray-400"><path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z" /><path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" /></svg>${escapeHTML(a.filename || `attachment ${a.part}`)} <span class="text-gray-400">${escapeHTML(formatSize(a.size))}</span></button>`).join("");
  const notes = [
    m.text_truncated ? "The text was too long to keep in full." : "",
    m.html_dropped ? "The original HTML was too large to keep; this is its text." : ""
  ].filter(Boolean).map((n) => `<p class="mt-2 text-xs text-gray-500 dark:text-gray-400">${n}</p>`).join("");
  const toggles = [
    isLongMessage(text) ? `<button type="button" data-inbox-toggle="full" data-inbox-message="${escapeHTML(m.id)}"${clamped ? " data-inbox-clamp-toggle hidden" : ""} class="text-xs font-medium text-paprika-700 hover:text-paprika-600 dark:text-paprika-400">${shown.full ? "Show less" : "Show more"}</button>` : "",
    hasQuote ? `<button type="button" data-inbox-toggle="quoted" data-inbox-message="${escapeHTML(m.id)}" class="text-xs font-medium text-paprika-700 hover:text-paprika-600 dark:text-paprika-400">${shown.quoted ? "Hide quoted text" : "Show quoted text"}</button>` : "",
    m.body_html ? `<button type="button" data-inbox-toggle="original" data-inbox-message="${escapeHTML(m.id)}" class="text-xs font-medium text-paprika-700 hover:text-paprika-600 dark:text-paprika-400">${shown.original ? "Hide original" : "Show original"}</button>` : ""
  ].filter(Boolean).join(`<span data-inbox-toggle-dot class="text-gray-300 dark:text-gray-600" aria-hidden="true">·</span>`);
  return `<li data-inbox-message-id="${escapeHTML(m.id)}" class="${ours ? "ml-8 rounded-lg bg-paprika-50/60 px-4 py-3 outline-1 outline-paprika-100 dark:bg-paprika-500/5 dark:outline-paprika-500/20" : "mr-8 rounded-lg bg-white px-4 py-3 outline-1 outline-gray-200 dark:bg-white/5 dark:outline-white/10"}${muted ? " opacity-70" : ""}">
    <div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
      <p class="text-sm font-semibold text-gray-900 dark:text-white">${ours ? `WeeknightBite${m.sent_by?.name ? ` <span class="font-normal text-gray-500 dark:text-gray-400">· ${escapeHTML(String(m.sent_by.name).split(/\s+/)[0])}</span>` : ""}` : escapeHTML(who)} <span class="ml-1 font-normal text-gray-500 dark:text-gray-400">${ours ? `to ${escapeHTML((m.to || []).map((a) => a.email || a.name).filter(Boolean).join(", "))}` : escapeHTML(m.from?.name ? m.from?.email || "" : "")}</span></p>
      <p class="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">${sourceBadgeHTML(via.source, { label: via.label })}<span title="${escapeHTML(fullTime(m.sent_at))}">${escapeHTML(fullTime(m.sent_at))}</span>${m.direction === "in" && !m.seen && !muted ? `<span class="${BADGE.paprika}">unread</span>` : ""}</p>
    </div>
    ${messageMetaHTML(m)}
    <div data-inbox-body="${escapeHTML(m.id)}"${clamped ? " data-inbox-clamped" : ""} class="mt-2 space-y-3 text-sm/6 break-words text-gray-900 dark:text-gray-100${clamped ? " max-h-72 overflow-hidden mask-b-from-75%" : ""}">${text.trim() ? messageBodyHTML(text) : `<span class="text-gray-400">(no text)</span>`}</div>
    ${shown.original ? `<div data-inbox-original="${escapeHTML(m.id)}" class="mt-3"></div>` : ""}
    ${notes}
    ${toggles ? `<div class="mt-2 flex items-center gap-2">${toggles}</div>` : ""}
    ${attachments ? `<div class="mt-3 flex flex-wrap gap-2">${attachments}</div>` : ""}
  </li>`;
}

/**
 * The page's messages once the customer's other conversations are read (#2660): every message of them all, oldest
 * first, with a divider (subject, source, status) where the conversation changes. The open one is highlighted and
 * has the "New" line; the others are dimmed, and one that is Handled or Spam shows its last message only until its
 * divider is asked to show the rest. Conversations beyond the cap are only counted.
 */
export function customerTimelineHTML(opened, messages, others, { lineAfter = null, shown = {}, myUid: uid = null, expanded = {}, hiddenThreads = 0 } = {}) {
  const entries = [];
  const add = (thread, list) => list.forEach((m) => entries.push({ thread, m, mine: thread.id === opened.id }));
  add(opened, messages);
  others.forEach((o) => add(o.thread, o.messages));
  entries.sort((a, b) => String(a.m.sent_at || "").localeCompare(String(b.m.sent_at || "")));
  // The cap drops the oldest messages of the other conversations first.
  let over = entries.length - MAX_TIMELINE_MESSAGES;
  const kept = over > 0 ? entries.filter((e) => e.mine || over-- <= 0) : entries;
  const collapsed = new Set(others.map((o) => o.thread).filter((t) => (t.status === "handled" || t.status === "spam") && !expanded[t.id]).map((t) => t.id));
  const lastOf = new Map();
  kept.forEach((e) => lastOf.set(e.thread.id, e.m.id));
  const hiddenIn = new Map();
  const shownEntries = kept.filter((e) => {
    if (!collapsed.has(e.thread.id) || lastOf.get(e.thread.id) === e.m.id) return true;
    hiddenIn.set(e.thread.id, (hiddenIn.get(e.thread.id) || 0) + 1);
    return false;
  });
  const countsAsNew = (m) => m.direction === "in" || Boolean(m.sent_by && m.sent_by.uid && m.sent_by.uid !== uid);
  const mineOnly = messages;
  const lineId = (lineAfter
    ? mineOnly.find((m) => m.sent_at > lineAfter && countsAsNew(m))
    : mineOnly.find((m) => m.direction === "in" && !m.seen))?.id;
  let previous = null;
  const items = [];
  shownEntries.forEach((e) => {
    if (previous !== e.thread.id) items.push(timelineDividerHTML(e.thread, { open: e.mine, hidden: hiddenIn.get(e.thread.id) || 0, expanded: Boolean(expanded[e.thread.id]), collapsible: !e.mine && (e.thread.status === "handled" || e.thread.status === "spam") }));
    previous = e.thread.id;
    if (e.mine && e.m.id === lineId) {
      items.push(`<li data-inbox-thread-new-line aria-label="New since you last opened this" class="flex items-center gap-2"><span class="h-px flex-1 bg-red-500"></span><span class="text-xs font-semibold text-red-600 dark:text-red-400">New</span><span class="h-px flex-1 bg-red-500"></span></li>`);
    }
    items.push(messageHTML(e.m, shown[e.m.id] || {}, { muted: !e.mine }));
  });
  const more = hiddenThreads > 0
    ? `<li class="text-center text-xs text-gray-500 dark:text-gray-400">${hiddenThreads} older conversation${hiddenThreads === 1 ? "" : "s"} not shown. <a href="${escapeHTML(historyHref(opened.user_uid ? { uid: opened.user_uid } : { email: opened.customer_email || opened.customer?.email || "" }))}" class="font-medium text-paprika-700 hover:text-paprika-600 dark:text-paprika-400">Full contact history</a></li>`
    : "";
  return `<ol class="flex flex-col gap-5">${more}${items.join("")}</ol>`;
}

/** Where the conversation changes: its subject (a button to open it, unless it is this page's), source, status. */
function timelineDividerHTML(t, { open, hidden, expanded, collapsible }) {
  const status = STATUS_LABELS[t.status] ? t.status : "open";
  const subject = escapeHTML(t.subject || "(no subject)");
  const title = open
    ? `<span class="text-sm font-semibold text-gray-900 dark:text-white">${subject}</span> <span class="text-xs text-paprika-700 dark:text-paprika-400">· this conversation</span>`
    : `<button type="button" data-open-thread="${escapeHTML(t.id)}" class="text-left text-sm font-semibold text-paprika-700 hover:underline dark:text-paprika-400">${subject}</button>`;
  const toggle = collapsible && (hidden > 0 || expanded)
    ? `<button type="button" data-inbox-expand-thread="${escapeHTML(t.id)}" class="text-xs font-medium text-paprika-700 hover:text-paprika-600 dark:text-paprika-400">${expanded ? "Hide earlier messages" : `Show ${hidden} earlier message${hidden === 1 ? "" : "s"}`}</button>`
    : "";
  return `<li data-inbox-thread-divider="${escapeHTML(t.id)}" class="${open ? "flex flex-wrap items-center gap-x-2 gap-y-1 border-t-2 border-paprika-300 pt-3 dark:border-paprika-500/50" : "flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-gray-200 pt-3 dark:border-white/10"}">${title}${sourceBadgeHTML(t.source || "email")}<span class="${STATUS_BADGES[status]}"><span aria-hidden="true" class="${STATUS_DOTS[status]}"></span>${STATUS_LABELS[status]}</span>${toggle}</li>`;
}

const REPLY_STATES = { PUBLISHED: "Published", PENDING_PUBLISH: "Waiting for Apple to publish", published: "Published" };

const HELP_RESULTS = { solved: "It solved it", not_solved: "It didn't solve it", none: "They didn't say whether it helped" };

/**
 * The help articles the app offered after a feedback (#2647): which, which they opened, whether one solved it. A solved
 * one comes in read.
 */
export function helpOfferedHTML(help) {
  if (!help) return "";
  const name = (slug) => { const w = String(slug).replace(/-+/g, " "); return w ? w[0].toUpperCase() + w.slice(1) : ""; };
  const shown = Array.isArray(help.shown) ? help.shown : [];
  const opened = Array.isArray(help.opened) ? help.opened : [];
  const text = shown.length
    ? `Help offered: ${shown.map(name).join(", ")}. Opened: ${opened.length ? opened.map(name).join(", ") : "none"}. ${HELP_RESULTS[help.outcome] || HELP_RESULTS.none}.`
    : "No help article fitted.";
  const tone = help.outcome === "solved" ? "text-green-700 dark:text-green-400" : "text-gray-500 dark:text-gray-400";
  return `<p class="mt-1 text-xs ${tone}" data-help-offered>${escapeHTML(text)}</p>`;
}

/** What a review or a feedback carries besides its text: stars and store details, or topics, app and device. */
export function messageMetaHTML(m) {
  const chip = (text) => `<span class="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600 dark:bg-white/10 dark:text-gray-300">${escapeHTML(text)}</span>`;
  if (m.review) {
    const r = m.review;
    const n = Math.max(0, Math.min(5, Number(r.rating) || 0));
    const bits = [r.territory, r.app_version ? `v${r.app_version}` : null, r.device, r.os_version ? `Android ${r.os_version}` : null, r.language].filter(Boolean);
    return `<div class="mt-1 flex flex-wrap items-center gap-1.5"><span class="text-sm tracking-wider text-amber-500" aria-label="${n} of 5 stars">${"★".repeat(n)}${"☆".repeat(5 - n)}</span>${bits.map(chip).join("")}</div>`;
  }
  if (m.review_reply) {
    // The badge beside the time already says "Public reply on …" (#1541): only its state here.
    return m.review_reply.state ? `<div class="mt-1 flex flex-wrap gap-1.5">${chip(REPLY_STATES[m.review_reply.state] || m.review_reply.state)}</div>` : "";
  }
  if (m.feedback) {
    const f = m.feedback;
    const bits = [...(f.topics || []), f.platform === "ios" ? "iOS" : f.platform === "android" ? "Android" : null,
      f.app_version ? `v${f.app_version}${f.build_number ? ` (${f.build_number})` : ""}` : null, f.device_model, f.os_version ? `OS ${f.os_version}` : null,
      f.email_source === "typed" ? "address they typed" : f.email_source === "account" ? "their account's email" : "no email"].filter(Boolean);
    return `<div class="mt-1 flex flex-wrap gap-1.5">${bits.map(chip).join("")}</div>${helpOfferedHTML(f.help)}`;
  }
  return "";
}

function customerPanelHTML(thread) {
  if (thread.automated) {
    return `<p class="text-xs text-gray-500 dark:text-gray-400">Sent by software (a newsletter, alert or notice), so it isn't linked to an account.</p>`;
  }
  const head = `<h3 class="mb-3 text-sm font-semibold text-gray-900 dark:text-white">Customer</h3>`;
  // Everything else we know about them (#1529), with or without an account; on a store review, also the field that
  // links it to an account by hand (#1925).
  const timeline = `<div id="inboxCustomerTimeline" class="mt-4"></div>`;
  if ((thread.source === "app_store" || thread.source === "google_play") && !thread.user_uid) {
    return `${head}<p class="text-sm text-gray-500 dark:text-gray-400">Store reviewers are anonymous: no account or address, only the name they chose.</p>${timeline}`;
  }
  if (!thread.user_uid) {
    return `${head}<p class="text-sm text-gray-500 dark:text-gray-400">No WeeknightBite account uses ${escapeHTML(thread.customer?.email || "this address")}.</p>${timeline}`;
  }
  return `${head}<p class="mb-3 text-sm"><a href="#users?uid=${encodeURIComponent(thread.user_uid)}" class="font-semibold text-paprika-700 hover:text-paprika-600 dark:text-paprika-400">Open in Users &amp; Diagnostics</a></p><div id="inboxFamilyContainer"></div>${timeline}`;
}

async function loadFamily(familyId) {
  const container = document.getElementById("inboxFamilyContainer");
  if (!container) return;
  if (familyState.familyId === familyId && (familyState.details || familyState.error)) {
    drawFamily();
    return;
  }
  familyState = { familyId, details: null, error: null, tab: familyState.familyId === familyId ? familyState.tab : "plan" };
  container.innerHTML = familyLoadingHTML(familyId);
  try {
    const details = await call("adminGetFamilyDetails", { familyId });
    if (familyState.familyId !== familyId) return;
    familyState.details = { ...details, fetched_at: new Date().toISOString() };
  } catch (err) {
    if (familyState.familyId !== familyId) return;
    familyState.error = err.message || String(err);
  }
  drawFamily();
}

function drawFamily() {
  const container = document.getElementById("inboxFamilyContainer");
  if (!container) return;
  const { familyId, details, error, tab } = familyState;
  container.innerHTML = error ? familyMessageHTML(familyId, "Could not read the family", escapeHTML(error), "red")
    : !details?.found ? familyMessageHTML(familyId, "No household found", "The account has no family document.")
    : familyCardHTML(details, tab, current?.thread?.user_uid || null);
  container.querySelectorAll("[data-family-tab]").forEach((btn) => btn.addEventListener("click", () => {
    familyState.tab = btn.dataset.familyTab;
    drawFamily();
  }));
  container.querySelector("[data-family-refresh]")?.addEventListener("click", () => {
    familyState.details = null;
    loadFamily(familyId);
  });
  container.querySelectorAll("[data-family-lookup-uid]").forEach((btn) => btn.addEventListener("click", () => {
    window.location.hash = `#users?uid=${encodeURIComponent(btn.dataset.familyLookupUid)}`;
  }));
}

function onThreadBodyClick(e) {
  if (!current) return;
  const toggle = e.target.closest?.("[data-inbox-toggle]");
  if (toggle) {
    const id = toggle.dataset.inboxMessage;
    const state = current.shown[id] || (current.shown[id] = {});
    state[toggle.dataset.inboxToggle] = !state[toggle.dataset.inboxToggle];
    const scroll = globalThis.scrollY;
    renderThread();
    if (scroll !== undefined) globalThis.scrollTo?.(0, scroll);
    // Show less from the end of a long message: back to where it starts, not somewhere further down the thread.
    if (toggle.dataset.inboxToggle === "full" && !state.full) {
      const li = document.querySelector(`[data-inbox-message-id="${CSS.escape(id)}"]`);
      const top = li ? li.getBoundingClientRect().top : 0;
      if (top < 80) globalThis.scrollBy?.(0, top - 80);
    }
    Object.keys(current.shown).filter((mid) => current.shown[mid].original).forEach(mountOriginal);
    return;
  }
  const expand = e.target.closest?.("[data-inbox-expand-thread]");
  if (expand) {
    const id = expand.dataset.inboxExpandThread;
    current.expanded = { ...current.expanded, [id]: !current.expanded?.[id] };
    const scroll = globalThis.scrollY;
    renderThread();
    if (scroll !== undefined) globalThis.scrollTo?.(0, scroll);
    return;
  }
  const open = e.target.closest?.("[data-inbox-thread-divider] [data-open-thread]");
  if (open) {
    openInboxThread(open.dataset.openThread);
    return;
  }
  const images = e.target.closest?.("[data-inbox-load-images]");
  if (images) {
    mountOriginal(images.dataset.inboxLoadImages, { images: true });
    return;
  }
  const attachment = e.target.closest?.("[data-inbox-attachment]");
  if (attachment) downloadAttachment(attachment.dataset.inboxMessage, attachment.dataset.inboxAttachment);
}

/**
 * The message's own HTML, sanitised, in a frame that runs no script (sandbox without allow-scripts or
 * allow-same-origin) and loads nothing remote until asked (its own CSP): remote images are often tracking pixels.
 */
/** Every message on the page: the open conversation's and the customer's other conversations'. */
function allMessages() {
  return current ? [...current.messages, ...(current.others || []).flatMap((o) => o.messages)] : [];
}

function mountOriginal(messageId, { images = false } = {}) {
  const host = document.querySelector(`[data-inbox-original="${messageId}"]`);
  const message = allMessages().find((m) => m.id === messageId);
  if (!host || !message || !message.body_html) return;
  host.innerHTML = `<div class="mb-2 flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400"><span>${images ? "Images loaded from the sender's servers." : "Remote images are blocked: they can tell the sender you opened it."}</span>${images ? "" : `<button type="button" data-inbox-load-images="${escapeHTML(messageId)}" class="font-medium text-paprika-700 hover:text-paprika-600 dark:text-paprika-400">Load images</button>`}</div>`;
  const frame = document.createElement("iframe");
  frame.setAttribute("sandbox", "allow-popups allow-popups-to-escape-sandbox");
  frame.setAttribute("referrerpolicy", "no-referrer");
  frame.setAttribute("title", "Original message");
  frame.className = "h-[32rem] w-full rounded-md bg-white outline-1 outline-gray-200 dark:outline-white/10";
  frame.srcdoc = originalSrcdoc(message.body_html, { images });
  host.appendChild(frame);
}

/** The frame's document: the sanitised HTML under a CSP that allows inline styles and, if asked, https images. */
export function originalSrcdoc(html, { images = false, sanitize = (h) => sanitizeEmailHtml(h) } = {}) {
  const csp = `default-src 'none'; style-src 'unsafe-inline'; font-src data:; img-src ${images ? "data: https:" : "data:"}`;
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}"><base target="_blank"><style>body{margin:12px;font:14px/1.5 system-ui,sans-serif;color:#111827;background:#fff}img{max-width:100%;height:auto}</style></head><body>${sanitize(html)}</body></html>`;
}

function sanitizeEmailHtml(html) {
  DOMPurify.removeHook("afterSanitizeAttributes");
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
  return DOMPurify.sanitize(String(html || ""), {
    FORBID_TAGS: ["form", "input", "button", "textarea", "select", "iframe", "frame", "object", "embed", "meta", "link", "base"],
    FORBID_ATTR: ["srcset", "action", "formaction"]
  });
}

async function downloadAttachment(messageId, part) {
  if (!current) return;
  const toast = firestoreCtx.showToast?.("Fetching the attachment from the mailbox…", "processing");
  try {
    const owner = allMessages().find((m) => m.id === messageId);
    const res = await call("getInboxAttachment", { threadId: owner?.threadId || current.thread.id, messageId, part });
    if (toast) firestoreCtx.dismissToast?.(toast);
    const link = document.createElement("a");
    link.href = res.url;
    link.download = res.filename || "attachment";
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (err) {
    if (toast) firestoreCtx.dismissToast?.(toast);
    firestoreCtx.showToast?.(`Couldn't fetch the attachment: ${escapeHTML(err.message)}`, "error");
  }
}

function setPressed(selector, dataKey, value) {
  document.querySelectorAll(selector).forEach((btn) => btn.setAttribute("aria-pressed", String(btn.dataset[dataKey] === value)));
}

function setText(id, value) {
  const el = document.getElementById(id);
  if (el) el.textContent = String(value);
}

export function relativeTime(iso, now = new Date()) {
  if (!iso) return "";
  const ms = now.getTime() - new Date(iso).getTime();
  if (isNaN(ms)) return "";
  const min = Math.round(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d} d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function fullTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
